import {
  Autocomplete,
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { useCallback, useEffect, useState } from 'react'
import { activityTemplateService } from '../../../../services/activityTemplateService'
import { useAuthContext } from '../../../../hooks/useAuthContext'
import adminDocumentTemplateService from '../../../../services/admin/document'
import { toast } from 'react-toastify'
import ConfirmationBox from '../../../../components/Dashboard/ConfirmationBox'
import { fieldTemplateService } from '../../../../services/fieldTemplateService'
import { FIELD_TYPES } from '../../../../utility/constants'
import { santizeHtml } from '../../../../utility/utilityFunctions'

const DocumentTemplates = () => {
  const { user } = useAuthContext()

  const initialModalData = {
    id: 0,
    document_template_name: '',
    activity_template_id: null,
    html_template: '',
    sequence: 1,
    repeater_table_field_template_id: null,
    grouping_table_config: [],
    is_landscape: false,
    is_draft_until_end_accepted: false,
    activities_for_approval_grid: [],
    trigger_route_template_ids: [],
  }

  const yesOrNoOptions = [
    { value: true, label: 'Yes' },
    { value: false, label: 'No' },
  ]

  const [loading, setLoading] = useState(false)
  const [buttonLoading, setButtonLoading] = useState(false)
  const [openModal, setOpenModal] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [modalData, setModalData] = useState({})
  const [allTemplates, setAllTemplates] = useState([])
  const [allActivities, setAllActivities] = useState([])
  const [approvalGridActivities, setApprovalGridActivities] = useState([])
  const [allRoutes, setAllRoutes] = useState([])
  const [allTables, setAllTables] = useState([])
  const [openDeleteGrouping, setOpenDeleteGrouping] = useState(false)
  const [deleteGroupingDetails, setDeleteGroupingDetails] = useState({ name: '', index: null })
  const [openDeleteTemplate, setOpenDeleteTemplate] = useState(false)
  const [deleteTemplateDetails, setDeleteTemplateDetails] = useState({ name: '', id: null })

  useEffect(() => {
    if (user) {
      ;(async () => {
        setLoading(true)
        const allDocumentTemplates =
          await adminDocumentTemplateService.getDocumentTemplatesOfCompany(user, user.company_id)
        const allActivitiesData = await activityTemplateService.getAllActivitiesOfCompany(
          user,
          user.company_id,
        )

        setAllTemplates(allDocumentTemplates)
        setAllActivities(allActivitiesData)
        setLoading(false)
      })()
    }
  }, [user])

  useEffect(() => {
    if (openModal && modalData.activity_template_id) {
      ;(async () => {
        const activityToRoutes = (
          await activityTemplateService.getAllToRoutes(user, modalData.activity_template_id)
        ).data
        const activityTableSubfields = (
          await fieldTemplateService.getTableSubfields(user, modalData.activity_template_id)
        ).data

        setAllRoutes(activityToRoutes)
        setAllTables(activityTableSubfields)
        if (
          modalData?.trigger_route_template_ids?.length == 0 ||
          modalData?.trigger_route_template_ids == null
        ) {
          setModalData({
            ...modalData,
            trigger_route_template_ids: activityToRoutes?.map((it) => it.route_template_id) || [],
          })
        }

        const selectedProcessTemplateId = allActivities.find(
          (it) => it.activity_template_id == modalData.activity_template_id,
        )?.process_template_id
        if (selectedProcessTemplateId) {
          setApprovalGridActivities(
            allActivities.filter((it) => it.process_template_id == selectedProcessTemplateId),
          )
        }
      })()
    } else {
      setAllRoutes([])
    }
  }, [user, openModal, modalData.activity_template_id])

  const getSubfieldsForGrouping = useCallback(
    (type, tableId) => {
      const tableData = allTables.find((it) => it.id == tableId)
      switch (type) {
        case 'grouping_subfield_ids':
          if (!!tableData) {
            return tableData?.subfields?.filter((sf) => sf.data_structure == FIELD_TYPES.LIST) || []
          }
          break
        case 'grouping_total_by_sft_ids':
          if (!!tableData) {
            return (
              tableData?.subfields?.filter((sf) =>
                [FIELD_TYPES.NUMBER, FIELD_TYPES.CURRENCY].includes(sf.data_structure),
              ) || []
            )
          }
          break

        default:
          break
      }
    },
    [allTables, modalData.grouping_sft_ids, modalData.grouping_total_by_sft_ids],
  )

  const handleOpenModal = (data = initialModalData) => {
    setModalData(data)
    setOpenModal(true)
  }

  const handleCloseModal = () => {
    setOpenModal(false)
    setModalData(initialModalData)
  }

  const handleChange = (type, value, index = null, subType = null) => {
    switch (type) {
      case 'grouping_table_config':
        if (index >= 0 && !!subType) {
          const config = modalData.grouping_table_config
          config[index][subType] = value
          if (subType == 'table_field_template_id') {
            config[index]['grouping_subfield_ids'] = []
            config[index]['grouping_total_by_sft_ids'] = []
          }
          setModalData({ ...modalData, [type]: config })
        }
        break
      default:
        setModalData({ ...modalData, [type]: value })
        break
    }
  }

  const handleCreateOrUpdateTemplate = async () => {
    try {
      if (
        modalData?.document_template_name.length == 0 ||
        modalData?.activity_template_id == null ||
        modalData?.html_template?.length == 0
      ) {
        return toast.error('Please fill the required fields')
      }
      setButtonLoading(true)
      const sanitizedHtml = santizeHtml(modalData.html_template)
      const updatedModalData = { ...modalData, html_template: sanitizedHtml }
      if (updatedModalData.id) {
        await adminDocumentTemplateService.updateDocumentTemplate(
          user,
          updatedModalData.id,
          updatedModalData,
        )

        const updatedAllTemplates = allTemplates.map((template) => {
          if (template.id == updatedModalData.id) {
            return updatedModalData
          }
          return template
        })
        toast.success('Template updated successfully!')
        setAllTemplates(updatedAllTemplates)
      } else {
        const result = await adminDocumentTemplateService.saveDocumentTemplate(
          user,
          updatedModalData,
        )
        toast.success('Template created successfully!')
        setAllTemplates([{ ...updatedModalData, id: result }, ...allTemplates])
      }
      handleCloseModal()
      setButtonLoading(false)
    } catch (error) {
      toast.error('Something went wrong')
      handleCloseModal()
      setButtonLoading(false)
    }
  }

  const handleDeleteTemplate = async () => {
    try {
      await adminDocumentTemplateService.deleteDocumentTemplate(user, deleteTemplateDetails.id)

      const updateAllTemplates = allTemplates.filter(
        (template) => template.id != deleteTemplateDetails.id,
      )

      setAllTemplates(updateAllTemplates)

      toast.error('Template deleted successfully!')

      setOpenDeleteTemplate(false)
      setDeleteTemplateDetails({ name: '', id: null })
    } catch (error) {
      toast.error('Something went wrong')
    }
  }

  const handleDeleteGroupingConfig = () => {
    let updatedGroupingConfig = modalData?.grouping_table_config?.filter((_, i) => {
      return i != deleteGroupingDetails.index
    })
    setModalData({ ...modalData, grouping_table_config: updatedGroupingConfig })
    setOpenDeleteGrouping(false)
    setDeleteGroupingDetails({ name: '', index: null })
  }

  const handleAddGroupingTables = () => {
    const tableGroupings = modalData?.grouping_table_config
    const newLineToBeAdded = {
      table_field_template_id: null,
      grouping_subfield_ids: [],
      grouping_total_by_sft_ids: [],
    }
    if (Array.isArray(tableGroupings) && tableGroupings.length > 0) {
      setModalData({ ...modalData, grouping_table_config: [...tableGroupings, newLineToBeAdded] })
    } else {
      setModalData({ ...modalData, grouping_table_config: [newLineToBeAdded] })
    }
  }

  if (loading) {
    return (
      <div
        style={{ height: '80vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <CircularProgress color="secondary" size={40} />
      </div>
    )
  }

  return (
    <Box mt={2}>
      <Box style={{ display: 'flex', gap: 16, marginBottom: '2rem' }}>
        <Typography variant="h5" fontWeight="bold">
          Document Templates
        </Typography>
      </Box>
      <Box>
        <Box mb="1rem" sx={{ display: 'flex', justifyContent: 'end' }}>
          <Button color="secondary" variant="contained" onClick={() => handleOpenModal()}>
            Create New Template
          </Button>
        </Box>

        <Box maxHeight={'65vh'} overflow={'scroll'}>
          {allTemplates.length > 0 ? (
            allTemplates.map((template) => (
              <Card elevation={1} style={{ padding: 16, margin: 8 }} key={template.id}>
                <Box
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Box>
                    <Tooltip title={template.document_template_name}>
                      <Typography variant="h6" style={{ margin: 8 }}>
                        #{template.id}. {template.document_template_name}
                      </Typography>
                    </Tooltip>
                  </Box>
                  <Box style={{ display: 'flex', columnGap: 8 }}>
                    <Tooltip title="Edit Template">
                      <IconButton
                        onClick={() => handleOpenModal(template)}
                        style={{ backgroundColor: '#f2eeed', height: 32, width: 32 }}
                        size="small"
                      >
                        <EditIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Template">
                      <IconButton
                        onClick={() => {
                          setOpenDeleteTemplate(true)
                          setDeleteTemplateDetails({
                            name: template.document_template_name,
                            id: template.id,
                          })
                        }}
                        style={{ backgroundColor: '#f2eeed', height: 32, width: 32 }}
                        size="small"
                      >
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Card>
            ))
          ) : (
            <>No Templates</>
          )}
        </Box>
      </Box>

      {/*  Add/Edit Template Modal  */}
      {openModal && (
        <Dialog open={openModal} fullWidth maxWidth="lg" onClose={handleCloseModal}>
          <DialogTitle>{modalData.id ? 'Edit Template' : 'Create Template'}</DialogTitle>
          <DialogContent>
            <Box p={'10px'} sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <TextField
                size="small"
                label="Document template name (required)"
                value={modalData.document_template_name}
                onChange={(e) => handleChange('document_template_name', e.target.value)}
                color="secondary"
              />
              <Autocomplete
                size="small"
                options={allActivities}
                getOptionLabel={(option) =>
                  `${option.process_template_name} / ${option.activity_template_name}`
                }
                value={
                  allActivities.find(
                    (activity) => activity.activity_template_id === modalData?.activity_template_id,
                  ) || null
                }
                isOptionEqualToValue={(option, value) =>
                  option.activity_template_id == value?.activity_template_id
                }
                onChange={(_, newValue) =>
                  handleChange('activity_template_id', newValue?.activity_template_id)
                }
                fullWidth
                renderInput={(params) => (
                  <TextField color="secondary" {...params} label="Select activity (required)" />
                )}
              />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  size="small"
                  label="HTML template (required)"
                  multiline
                  fullWidth
                  minRows={7}
                  maxRows={15}
                  value={modalData.html_template}
                  onChange={(e) => handleChange('html_template', e.target.value)}
                  color="secondary"
                  placeholder="Paste your HTML here..."
                />
                <Tooltip title="HTML Template Preview">
                  <IconButton
                    onClick={() => setShowPreview(true)}
                    style={{ backgroundColor: '#f2eeed', height: 32, width: 32 }}
                    size="small"
                  >
                    <VisibilityIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
              </Box>

              <Box sx={{ display: 'flex', gap: '16px', marginTop: '8px' }}>
                <TextField
                  size="small"
                  label="Sequence"
                  value={modalData.sequence}
                  onChange={(e) => handleChange('sequence', e.target.value)}
                  type="number"
                  color="secondary"
                />
                <Autocomplete
                  size="small"
                  options={yesOrNoOptions}
                  getOptionLabel={(option) => option.label}
                  value={
                    yesOrNoOptions.find((bool) => bool.value === modalData?.is_landscape) || null
                  }
                  isOptionEqualToValue={(option, value) => option?.value == value?.value}
                  onChange={(_, newValue) => handleChange('is_landscape', newValue.value)}
                  fullWidth
                  renderInput={(params) => (
                    <TextField color="secondary" {...params} label="Is Landscape" />
                  )}
                />
                <Autocomplete
                  size="small"
                  options={yesOrNoOptions}
                  getOptionLabel={(option) => option.label}
                  value={
                    yesOrNoOptions.find(
                      (bool) => bool.value === modalData?.is_draft_until_end_accepted,
                    ) || null
                  }
                  isOptionEqualToValue={(option, value) => option.value == value?.value}
                  onChange={(_, newValue) =>
                    handleChange('is_draft_until_end_accepted', newValue.value)
                  }
                  fullWidth
                  renderInput={(params) => (
                    <TextField color="secondary" {...params} label="Is draft until end accept" />
                  )}
                />
              </Box>

              <Autocomplete
                size="small"
                options={allTables}
                getOptionLabel={(option) => option.field_template_name}
                value={
                  allTables.find(
                    (table) => table.id === modalData?.repeater_table_field_template_id,
                  ) || null
                }
                isOptionEqualToValue={(option, value) => option?.id == value?.id}
                onChange={(_, newValue) =>
                  handleChange('repeater_table_field_template_id', newValue?.id)
                }
                fullWidth
                renderInput={(params) => (
                  <TextField color="secondary" {...params} label="Repeater table field" />
                )}
              />

              <Box
                sx={{
                  position: 'relative',
                  border: '1px solid rgba(0, 0, 0, 0.3)',
                  padding: '16px',
                  borderRadius: '4px',
                  display: 'inline-block',
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    position: 'absolute',
                    top: '-12px',
                    left: '16px',
                    background: '#fff',
                    padding: '0 8px',
                    color: 'rgba(0, 0, 0, 0.4)',
                    fontSize: '13px',
                  }}
                >
                  Table grouping config
                </Typography>
                {Array.isArray(modalData.grouping_table_config) &&
                  modalData.grouping_table_config.map((config, index) => (
                    <Box
                      key={index}
                      sx={{ display: 'flex', gap: '16px', marginTop: '8px', marginBottom: '8px' }}
                    >
                      <Autocomplete
                        size="small"
                        options={allTables || []}
                        getOptionLabel={(option) => option.field_template_name}
                        value={
                          allTables.find((table) => table.id === config.table_field_template_id) ||
                          null
                        }
                        isOptionEqualToValue={(option, value) => option?.id == value?.id}
                        onChange={(_, newValue) =>
                          handleChange(
                            'grouping_table_config',
                            newValue?.id,
                            index,
                            'table_field_template_id',
                          )
                        }
                        fullWidth
                        renderInput={(params) => (
                          <TextField color="secondary" {...params} label="Grouping table field" />
                        )}
                      />
                      {/* Grouping Subfields */}
                      <Autocomplete
                        size="small"
                        options={
                          getSubfieldsForGrouping(
                            'grouping_subfield_ids',
                            config.table_field_template_id,
                          ) || []
                        }
                        getOptionLabel={(option) => option.field_template_name}
                        value={
                          getSubfieldsForGrouping(
                            'grouping_subfield_ids',
                            config.table_field_template_id,
                          )?.filter((it) => config?.grouping_subfield_ids?.includes(it.id)) || []
                        }
                        isOptionEqualToValue={(option, value) => option?.id == value?.id}
                        onChange={(_, newValue) =>
                          handleChange(
                            'grouping_table_config',
                            newValue?.map((it) => it.id),
                            index,
                            'grouping_subfield_ids',
                          )
                        }
                        multiple
                        fullWidth
                        renderInput={(params) => (
                          <TextField color="secondary" {...params} label="Grouping subfields" />
                        )}
                      />
                      {/* Grouping total by subfields */}
                      <Autocomplete
                        size="small"
                        options={
                          getSubfieldsForGrouping(
                            'grouping_total_by_sft_ids',
                            config.table_field_template_id,
                          ) || []
                        }
                        getOptionLabel={(option) => option.field_template_name}
                        value={
                          getSubfieldsForGrouping(
                            'grouping_total_by_sft_ids',
                            config.table_field_template_id,
                          )?.filter((it) => config?.grouping_total_by_sft_ids?.includes(it.id)) ||
                          []
                        }
                        isOptionEqualToValue={(option, value) => option?.id == value?.id}
                        onChange={(_, newValue) => {
                          handleChange(
                            'grouping_table_config',
                            newValue?.map((it) => it.id),
                            index,
                            'grouping_total_by_sft_ids',
                          )
                        }}
                        multiple
                        fullWidth
                        renderInput={(params) => (
                          <TextField
                            color="secondary"
                            {...params}
                            label="Grouping total by subfields"
                          />
                        )}
                      />
                      <Tooltip title="Delete">
                        <IconButton
                          onClick={() => {
                            setOpenDeleteGrouping(true)
                            setDeleteGroupingDetails({
                              name:
                                allTables.find((it) => it.id == config.table_field_template_id)
                                  ?.field_template_name || '',
                              index,
                            })
                          }}
                          size="small"
                        >
                          <DeleteIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ))}

                <Button variant="outlined" onClick={handleAddGroupingTables} color="secondary">
                  Add
                </Button>
              </Box>

              <Autocomplete
                size="small"
                options={allRoutes || []}
                getOptionLabel={(option) => option?.route_template_name || ''}
                value={
                  allRoutes
                    ? allRoutes.filter((route) =>
                        modalData?.trigger_route_template_ids?.includes(route.route_template_id),
                      )
                    : []
                }
                isOptionEqualToValue={(option, value) =>
                  option?.route_template_id === value?.route_template_id
                }
                onChange={(_, newValue) =>
                  handleChange(
                    'trigger_route_template_ids',
                    newValue?.map((it) => it.route_template_id),
                  )
                }
                fullWidth
                multiple
                renderInput={(params) => (
                  <TextField color="secondary" {...params} label="Trigger routes" />
                )}
              />
              <Autocomplete
                size="small"
                options={approvalGridActivities || []}
                getOptionLabel={(option) => option?.activity_template_name || ''}
                value={
                  approvalGridActivities
                    ? approvalGridActivities.filter((activity) =>
                        modalData?.activities_for_approval_grid?.includes(
                          activity.activity_template_id,
                        ),
                      )
                    : []
                }
                isOptionEqualToValue={(option, value) =>
                  option?.activity_template_id === value?.activity_template_id
                }
                onChange={(_, newValue) =>
                  handleChange(
                    'activities_for_approval_grid',
                    newValue?.map((it) => it.activity_template_id),
                  )
                }
                fullWidth
                multiple
                renderInput={(params) => (
                  <TextField color="secondary" {...params} label="Approval grid activities" />
                )}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" color="info" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="secondary"
              disabled={buttonLoading}
              onClick={handleCreateOrUpdateTemplate}
            >
              {modalData.id ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/*  Preview Template Body Modal  */}
      <Dialog open={showPreview} fullWidth maxWidth="lg" onClose={() => setShowPreview(false)}>
        <DialogTitle>HTML Template Preview</DialogTitle>
        <DialogContent>
          <div
            dangerouslySetInnerHTML={{
              __html:
                modalData.html_template?.trim()?.length > 0
                  ? santizeHtml(modalData.html_template)
                  : `<p>No Preview available.</p>`,
            }}
          ></div>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="info" onClick={() => setShowPreview(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Static file Confirmation */}
      <ConfirmationBox
        isDialogOpen={openDeleteGrouping}
        setIsDialogOpen={setOpenDeleteGrouping}
        title={`Are you sure you want to delete ${
          deleteGroupingDetails?.name.length > 0 ? `"${deleteGroupingDetails?.name}" ` : ''
        }?`}
        onYes={handleDeleteGroupingConfig}
      />

      {/* Delete Template Confirmation */}
      <ConfirmationBox
        isDialogOpen={openDeleteTemplate}
        setIsDialogOpen={setOpenDeleteTemplate}
        title={`Are you sure you want to delete "${deleteTemplateDetails?.name}" ?`}
        onYes={handleDeleteTemplate}
      />
    </Box>
  )
}

export default DocumentTemplates
